import { useEffect } from 'react';

import { useUserPreferences } from '../user-preferences/hooks/useUserPreferences';
import {
  type ColorModePreference,
  isColorModePreference,
  PreferenceScope,
  UserPreferenceKey,
} from '../user-preferences/types';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/**
 * localStorage cache of the resolved theme, read by the first-paint anti-flash
 * script in index.html before React mounts. Only written while the
 * DashboardNewLayout flag is on; when the flag is off the sync clears it (see
 * below), so flag-off users never keep a stale marker.
 */
const DATA_THEME_STORAGE_KEY = 'revelio-data-theme';

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_MEDIA_QUERY).matches
  );
}

function resolveColorMode(
  preference: Exclude<ColorModePreference, 'legacy'>
): 'light' | 'dark' {
  if (preference === 'system') {
    return prefersDark() ? 'dark' : 'light';
  }
  return preference;
}

/**
 * Writes the `data-theme` marker and caches it for the anti-flash script.
 * `<html>` always carries a value: Chakra declares its :root semantic tokens
 * under `:root[data-theme]`, so dropping it leaves them undefined (white
 * surfaces go transparent, borders go black) — Legacy keeps it on `light`.
 * `<body>` gates the new-theme tokens.css overrides (scoped to
 * `body[data-theme]`), so Legacy drops only that marker.
 */
function applyTheme(theme: 'light' | 'dark' | null) {
  document.documentElement.setAttribute('data-theme', theme ?? 'light');
  document.documentElement.style.colorScheme = theme ?? 'light dark';
  if (theme === null) {
    document.body.removeAttribute('data-theme');
  } else {
    document.body.setAttribute('data-theme', theme);
  }
  try {
    if (theme === null) {
      localStorage.removeItem(DATA_THEME_STORAGE_KEY);
    } else {
      localStorage.setItem(DATA_THEME_STORAGE_KEY, theme);
    }
  } catch {
    // localStorage unavailable — the anti-flash script just won't pre-apply.
  }
}

/**
 * Bridges the backend `ColorMode` user preference (System default / Light /
 * Dark / Legacy) into the `data-theme` marker — the sole dashboard theming
 * mechanism (see tokens.css and the component CSS that keys off
 * `body[data-theme]`). Resolves `system` against `prefers-color-scheme` and
 * follows live OS changes; `legacy` removes the marker so components render with
 * the pre-redesign styling.
 *
 * Always mounted; `enabled` carries the DashboardNewLayout flag. When the flag
 * is off it clears the marker and the cached storage key, so a roll-back after
 * the flag had been on can't leave a stale `body[data-theme]` (re-applied by the
 * anti-flash script) stuck on for the session. Otherwise flag-off users stay on
 * the legacy :root tokens with zero behavior change.
 */
export function ColorModePreferenceSync({ enabled }: { enabled: boolean }) {
  const { preferences } = useUserPreferences(PreferenceScope.GLOBAL);
  const stored: unknown = preferences[UserPreferenceKey.ColorMode];
  const preference: ColorModePreference = isColorModePreference(stored)
    ? stored
    : 'legacy';

  useEffect(() => {
    if (!enabled) {
      // Flag off (incl. a roll-back after it was on): undo any marker the
      // anti-flash script restored from a stale storage value and clear the key
      // so future first-paints don't re-apply it. applyTheme(null) drops the
      // <body> marker, resets <html> to light and removes the storage key.
      applyTheme(null);
      return;
    }
    applyTheme(preference === 'legacy' ? null : resolveColorMode(preference));
  }, [enabled, preference]);

  // On unmount revert to the legacy baseline: keep <html data-theme="light">
  // (Chakra needs it for its :root tokens, see applyTheme) and drop only the
  // <body> marker. Leave the localStorage cache intact to avoid a first-paint
  // flash on the next load.
  useEffect(() => {
    return () => {
      document.documentElement.setAttribute('data-theme', 'light');
      document.documentElement.style.colorScheme = 'light dark';
      document.body.removeAttribute('data-theme');
    };
  }, []);

  // While in `system` mode, follow live OS theme changes.
  useEffect(() => {
    if (!enabled || preference !== 'system') {
      return;
    }
    const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      applyTheme(event.matches ? 'dark' : 'light');
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [enabled, preference]);

  return null;
}
